import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Disc3, Settings as SettingsIcon, LogOut, CreditCard, Clock, ChevronDown, User, Home, Shield, Link2, MessageSquare } from 'lucide-react';

export default function Navbar() {
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMinutes = userProfile?.minutes || 0;
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const response = await api.get('/messages/unread-count');
      setUnread(response.data.unread || 0);
    } catch (error) {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 20000);
    return () => clearInterval(interval);
  }, [user, fetchUnread, location.pathname]);

  const getFirstName = () => {
    if (user?.first_name) return user.first_name;
    if (userProfile?.name) return userProfile.name.split(' ')[0];
    return user?.email?.split('@')[0] || 'User';
  };

  const getFullName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) return user.first_name;
    if (userProfile?.name) return userProfile.name;
    return 'User';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center space-x-6">
            <div 
              className="flex items-center space-x-2 cursor-pointer" 
              onClick={() => navigate('/dashboard')}
            >
              <Disc3 className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold">RecordRinse</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-1">
              {!isActive('/dashboard') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/messages')}
                className="relative"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Messages
                {unread > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold">
                    {unread}
                  </span>
                )}
              </Button>
            </nav>
          </div>
          
          <div className="flex items-center space-x-2">
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    {user.profile_image_url ? (
                      <img 
                        src={user.profile_image_url} 
                        alt={user.first_name || 'User'} 
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                      {getFirstName()}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{getFullName()}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Minutes Available</span>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-primary">{userMinutes.toFixed(1)} min</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  
                  <div className="md:hidden">
                    {!isActive('/dashboard') && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/dashboard')}
                        className="cursor-pointer"
                      >
                        <Home className="h-4 w-4 mr-2" />
                        <span>Dashboard</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate('/messages')}
                      className="cursor-pointer"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      <span>Messages</span>
                      {unread > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold">
                          {unread}
                        </span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </div>
                  
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <CreditCard className="h-4 w-4 mr-2 text-primary" />
                    <span>Buy More Minutes</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    <span>Account Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/affiliate')}
                    className="cursor-pointer"
                  >
                    <Link2 className="h-4 w-4 mr-2 text-green-500" />
                    <span>Affiliate Program</span>
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer"
                    >
                      <Shield className="h-4 w-4 mr-2 text-yellow-500" />
                      <span>Admin Panel</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-red-500 focus:text-red-500"
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
